import React, { useState, useEffect } from "react";
import { useReducedMotion, motion } from "framer-motion";
import { postData } from "../api/protectedApi";
import { handleCopy } from "../utils/handleCopy";
import { getDashboardVariants } from "../component/dashboard/dashboardAnimations";
import DashboardHeader from "../component/dashboard/DashboardHeader";
import StatCardsRow from "../component/dashboard/StatCardsRow";
import RecentTransactionsTable from "../component/dashboard/RecentTransactionsTable";
import WalletCardPanel from "../component/dashboard/WalletCardPanel";
import SystemHighlightsCard from "../component/dashboard/SystemHighlightsCard";
import styles from "./css/Dashboard.module.css";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    walletBalance: 0,
    depositCount: 0,
    totalDeposit: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [totalTransactionsCount, setTotalTransactionsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedText, setCopiedText] = useState(null);

  const shouldReduceMotion = useReducedMotion();
  const variants = getDashboardVariants(shouldReduceMotion);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const [walletRes, depositRes] = await Promise.allSettled([
        postData("/user/wallet_sum", {}),
        postData("/user/deposit_transactions", { skip: "0", limit: "5" }),
      ]);

      if (walletRes.status === "fulfilled" && walletRes.value?.data) {
        setDashboardData((prev) => ({
          ...prev,
          walletBalance: walletRes.value.data.walletBalance || 0,
          totalDeposit: walletRes.value.data.totalDeposit || 0,
          depositCount: walletRes.value.data.depositCount || 0,
        }));
      }

      if (depositRes.status === "fulfilled" && depositRes.value?.data) {
        const dataList = Array.isArray(depositRes.value.data.data)
          ? depositRes.value.data.data
          : [];
        setTransactions(dataList);
        setTotalTransactionsCount(depositRes.value.data.count || dataList.length);
      }
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const onCopy = (text, e) => {
    if (e) e.stopPropagation();
    handleCopy(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  return (
    <div className={styles.dashboardContainer}>
      <DashboardHeader
        isLoading={isLoading}
        headerContainerVariants={variants.headerContainerVariants}
        textFadeUpVariants={variants.textFadeUpVariants}
      />

      <StatCardsRow
        dashboardData={dashboardData}
        isLoading={isLoading}
        statContainerVariants={variants.statContainerVariants}
        statCardVariants={variants.statCardVariants}
        textFadeUpVariants={variants.textFadeUpVariants}
      />

      <div className={`row g-3 ${styles.mainGridRow}`}>
        <RecentTransactionsTable
          displayedTransactions={transactions}
          totalTransactionsCount={totalTransactionsCount}
          isLoading={isLoading}
          copiedText={copiedText}
          handleCopy={onCopy}
          sectionLeftVariants={variants.sectionLeftVariants}
          tableBodyVariants={variants.tableBodyVariants}
          tableRowVariants={variants.tableRowVariants}
          textFadeUpVariants={variants.textFadeUpVariants}
        />

        <motion.div
          className={`col-12 col-lg-4 ${styles.rightPanelCol}`}
          variants={variants.sectionRightVariants}
          initial="hidden"
          animate={isLoading ? "hidden" : "visible"}
        >
          <WalletCardPanel
            dashboardData={dashboardData}
            textFadeUpVariants={variants.textFadeUpVariants}
          />
          <SystemHighlightsCard
            textFadeUpVariants={variants.textFadeUpVariants}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;

